import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';

import { useAddressInputLabels } from '@klareospace/pharos-lib-front-utils';
import type { AddressFormInput, AddressInputInitialAddress } from '@klareospace/pharos-lib-front-utils';
import {
  AddressInput,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  Switch,
} from '@klareospace/pharos-lib-web-utils';
import type { GeoCoordinates, GeocodingSource } from '@klareospace/pharos-lib-types';

import { HERE_ATTRIBUTION, HERE_TILE_URL } from '&/config';

import type { AddressBookEntryInput, ContactField, CustomerFormInput } from '../schemas/customer.schema';
import { buildEmptyAddressEntry } from '../utils/customer-form.utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressFormProps {
  form: UseFormReturn<CustomerFormInput>;
  handleContactFieldChange: <TField extends ContactField>(
    index: number,
    field: TField,
    value: AddressBookEntryInput[TField],
  ) => void;
  index: number;
  initialAddress?: AddressInputInitialAddress;
}

/**
 * Shape the shared `AddressInput` component expects at the *root* of the form it is bound to
 * (`address`, `coordinates`, `geocodingScore`, `geocodingRawScore`, `geocodingSource`) — see
 * `useAddressInputBridge` below for why this can't be the indexed `addresses.${index}` path
 * directly.
 */
interface AddressInputBridgeValues {
  address: AddressFormInput;
  coordinates?: Partial<GeoCoordinates>;
  geocodingRawScore?: number;
  geocodingScore?: number;
  geocodingSource?: GeocodingSource;
}

// ─── AddressInput bridge ────────────────────────────────────────────────────
// AddressInput hardcodes the field paths it reads/writes ('address.*', 'coordinates.*',
// 'geocodingSource'...) against the *root* of the form it receives, so it cannot bind directly to
// an indexed `addresses.${index}.*` path on the parent CustomerFormInput. This local bridge form
// exposes exactly the root shape AddressInput expects — seeded once from the entry's current
// values — and mirrors every change back into the parent field array entry, so the parent form
// (dirty state, validation, submit payload) stays the single source of truth.
function useAddressInputBridge(
  parentForm: UseFormReturn<CustomerFormInput>,
  index: number,
): UseFormReturn<AddressInputBridgeValues> {
  // Transiently undefined on drawer close, before the reset-to-create-defaults effect runs.
  const entry = parentForm.getValues(`addresses.${index}`) ?? buildEmptyAddressEntry();
  const bridgeForm = useForm<AddressInputBridgeValues>({
    defaultValues: {
      address: entry.address,
      coordinates: entry.coordinates,
      geocodingRawScore: entry.geocodingRawScore,
      geocodingScore: entry.geocodingScore,
      geocodingSource: entry.geocodingSource,
    },
  });

  useEffect(() => {
    const subscription = bridgeForm.watch((values) => {
      parentForm.setValue(`addresses.${index}.address`, values.address as AddressFormInput, {
        shouldDirty: true,
        shouldValidate: true,
      });
      parentForm.setValue(`addresses.${index}.coordinates`, values.coordinates, {
        shouldDirty: true,
        shouldValidate: true,
      });
      parentForm.setValue(`addresses.${index}.geocodingScore`, values.geocodingScore, {
        shouldDirty: true,
        shouldValidate: true,
      });
      parentForm.setValue(`addresses.${index}.geocodingRawScore`, values.geocodingRawScore, {
        shouldDirty: true,
        shouldValidate: true,
      });
      parentForm.setValue(`addresses.${index}.geocodingSource`, values.geocodingSource, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
    return (): void => subscription.unsubscribe();
  }, [bridgeForm, parentForm, index]);

  return bridgeForm;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Per-entry address-book form: contact-override toggle, contactName/contactPhone (when the
 * toggle is off), and the geocoded address itself. Used for the always-open create-mode entry,
 * for a newly-added entry, and for a collapsed card expanded via its "Edit" action.
 */
export function AddressForm({
  form,
  handleContactFieldChange,
  index,
  initialAddress,
}: AddressFormProps): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const addressInputLabels = useAddressInputLabels({
    keyPrefix: 'spaces.settings.sites.address',
    namespace: 'settings',
  });
  const bridgeForm = useAddressInputBridge(form, index);
  const isCustomerContact = useWatch({ control: form.control, name: `addresses.${index}.isCustomerContact` });

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name={`addresses.${index}.isCustomerContact`}
        render={({ field }): React.JSX.Element => (
          <FormItem className="flex flex-row items-center justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-0.5">
              <FormLabel>{t('form.contactToggle.label')}</FormLabel>
              <FormDescription>{t('form.contactToggle.subtitle')}</FormDescription>
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />

      {!isCustomerContact && (
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name={`addresses.${index}.contactName`}
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('form.contactName')} <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(event): void => handleContactFieldChange(index, 'contactName', event.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`addresses.${index}.contactPhone`}
            render={({ field }): React.JSX.Element => (
              <FormItem>
                <FormLabel>
                  {t('form.contactPhone')} <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <PhoneInput
                    countryAriaLabel={tCommon('phoneInput.countryAriaLabel')}
                    emptyText={tCommon('phoneInput.noCountryFound')}
                    locale={i18n.language}
                    onBlur={field.onBlur}
                    onChange={(value): void => handleContactFieldChange(index, 'contactPhone', value)}
                    searchPlaceholder={tCommon('phoneInput.searchPlaceholder')}
                    value={field.value ?? null}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      <div className="space-y-3">
        <span className="block text-sm font-medium mb-2">{t('form.sections.addressLine', { index: index + 1 })}</span>
        <Form {...bridgeForm}>
          <AddressInput<AddressInputBridgeValues>
            control={bridgeForm.control}
            initialAddress={initialAddress}
            labels={addressInputLabels}
            onSetValue={(field, value): void =>
              bridgeForm.setValue(field, value as never, { shouldDirty: true, shouldValidate: true })
            }
            tileAttribution={HERE_ATTRIBUTION}
            tileUrl={HERE_TILE_URL}
          />
        </Form>
      </div>
    </div>
  );
}
